import { twMerge } from "tailwind-merge";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  H5,
  H6,
} from "@sellar-io/components";
import { CaseSizeRuleListResponseType } from "@sellar-io/api-v2/types";
import { useState } from "react";
import { Icon } from "../../components";
import {
  TableLegacy,
  TableLegacyContainer,
  TableLegacyTHead,
  TableLegacyTR,
  TableLegacyTH,
  TableLegacyTBody,
  TableLegacyTD,
} from "../../components/table/TableLegacy";
import { useCaseSizeRuleList } from "../../hooks/api/v2/caseSizeRule/useCaseSizeRuleList";
import { useSupplierUpdate } from "../../hooks/api/v2/supplier/useSupplierUpdate";
import { useRemoteFeatureFlags } from "../../hooks/useRemoteFeatureFlags";
import { useSellarCurrentBusiness } from "../../hooks/useSellarCurrentBusiness.shared";
import { useSellarSupplier } from "../../hooks/useSellarSupplier";
import {
  RadioGroup,
  RadioGroupOption,
  RadioGroupContainer,
} from "../../components/radioSelector/RadioGroup";
import { generateProductItemName } from "../deliverySettings/helpers/generateProductItemName";
import { CaseSizeRuleUpdateDialog } from "../deliverySettings/dialogs/CaseSizeRuleUpdateDialog";
import { CaseSizeRuleCreateDialog } from "../deliverySettings/dialogs/CaseSizeRuleCreateDialog/CaseSizeRuleCreateDialog";
import { Delivery } from "./Delivery";

type CaseSizeRule = CaseSizeRuleListResponseType[number];

const usePackaging = () => {
  const caseSizeRuleListQuery = useCaseSizeRuleList({
    sortBy: "createdAt",
    sortDir: "desc",
  });

  return {
    rules: caseSizeRuleListQuery.data?.pages[0],
  };
};

const ProductItem = ({
  containerType,
  volume,
  unit,
}: {
  containerType: "can" | "bottle";
  volume: number;
  unit: string;
}) => {
  const icon = containerType === "can" ? "CAN" : "BOTTLE";
  const name = generateProductItemName({ containerType, volume, unit });

  return (
    <span className="flex flex-wrap items-center space-x-2">
      <Icon size={1.3} icon={icon} />
      <span>{name}</span>
    </span>
  );
};

const TableRow = ({ caseSizeRule }: { caseSizeRule: CaseSizeRule }) => {
  const [isModalActive, setIsModalActive] = useState(false);
  return (
    <TableLegacyTR>
      <TableLegacyTD>
        <ProductItem {...caseSizeRule} />
      </TableLegacyTD>
      <TableLegacyTD>{caseSizeRule.quantity}</TableLegacyTD>
      <TableLegacyTD>
        <span className="flex justify-end">
          <Button color={"transparent"} onClick={() => setIsModalActive(true)}>
            <Icon icon={"PENCIL"} />
          </Button>
        </span>
      </TableLegacyTD>

      <CaseSizeRuleUpdateDialog
        caseSizeRule={caseSizeRule}
        active={isModalActive}
        onDismiss={() => setIsModalActive(false)}
      />
    </TableLegacyTR>
  );
};

const FullCaseRequirement = () => {
  const supplier = useSellarSupplier();
  const updateSupplierMutation = useSupplierUpdate(supplier.id);
  const [fullCaseRequired, setFullCaseRequired] = useState<boolean>(
    supplier.enforceCaseSizeRules,
  );

  const onChange = (value: boolean) => {
    setFullCaseRequired(value);
    updateSupplierMutation.mutate(
      {
        enforceCaseSizeRules: value,
      },
      {
        onError: () => setFullCaseRequired(!value),
      },
    );
  };

  return (
    <Card className="bg-white">
      <CardHeader
        className={twMerge("bg-white", "flex items-center justify-between p-4")}
      >
        <div className="flex flex-col">
          <H5>Full case requirement</H5>
          <H6>
            Make it a requirement for your customers to order in full cases,
            i.e. in the quantities you have listed under ‘Case size
            definitions’.
          </H6>
        </div>
      </CardHeader>
      <CardContent className={"p-4"}>
        <RadioGroup
          value={`${fullCaseRequired}`}
          onChange={(value) => onChange(value === "true")}
        >
          <RadioGroupContainer>
            <RadioGroupOption value={"true"}>
              <div className="flex flex-col">
                <H5>Yes, I require my customers to order in full cases</H5>
                <p>
                  Your customers will not be able to proceed to checkout until
                  the items in their basket add up to full cases.
                </p>
              </div>
            </RadioGroupOption>
            <RadioGroupOption value={"false"}>
              <div className="flex flex-col">
                <H5>No, I don't require my customers to order in full cases</H5>
                <p>
                  Your customers will be able to proceed to checkout even if the
                  packaged items in their basket don't add up to full cases, as
                  long as they've met any MOQs you have set.
                </p>
              </div>
            </RadioGroupOption>
          </RadioGroupContainer>
        </RadioGroup>
      </CardContent>
    </Card>
  );
};

type CollectionOnCheckoutType = "collection" | "business" | "none";
export const DeliverySettingsV2 = () => {
  const { rules } = usePackaging();
  const [isModalActive, setIsModalActive] = useState(false);
  const { business } = useSellarCurrentBusiness();
  const supplier = useSellarSupplier();
  const updateSupplierMutation = useSupplierUpdate(supplier.id);
  const collectionOnCheckoutType = !supplier.allowCollectionOnCheckout
    ? "none"
    : supplier.allowCollectionFromAddressId === business?.CollectionAddressId
      ? "collection"
      : "business";
  const [collectionOnCheckout, setCollectionOnCheckout] =
    useState<CollectionOnCheckoutType>(collectionOnCheckoutType);

  const collectionAddressCreationEnabled = useRemoteFeatureFlags().isActive(
    "collectionAddressCreation",
  );

  const onChange = (value: CollectionOnCheckoutType) => {
    const allowCollectionOnCheckout =
      value === "collection" || value === "business";
    setCollectionOnCheckout(value);

    const collectionFromAddressId =
      value === "collection"
        ? business?.CollectionAddressId
        : value === "business"
          ? business?.DeliveryAddressId
          : null;
    updateSupplierMutation.mutate(
      {
        allowCollectionOnCheckout: allowCollectionOnCheckout,
        allowCollectionFromAddressId: collectionFromAddressId,
      },
      {
        onError: () => setCollectionOnCheckout(collectionOnCheckoutType),
      },
    );
  };
  return (
    <Delivery>
      <div className="flex flex-col gap-4">
        <Card className="bg-white">
          <CardHeader
            className={twMerge(
              "bg-white",
              "flex items-center justify-between p-4",
            )}
          >
            <div className="flex flex-col">
              <H5>Case size definitions</H5>
              <H6>
                Define what you consider a{" "}
                <span className="font-bold">full case</span> of each of your
                bottle and can products. Cases will be used to more accurately
                track minimum order quantities and volume discounts.
              </H6>
            </div>
          </CardHeader>
          <CardContent className={"p-4"}>
            <div>
              <TableLegacyContainer>
                <TableLegacy>
                  <TableLegacyTHead>
                    <TableLegacyTR>
                      <TableLegacyTH>Product item</TableLegacyTH>
                      <TableLegacyTH>Quantity</TableLegacyTH>
                      <TableLegacyTH></TableLegacyTH>
                    </TableLegacyTR>
                  </TableLegacyTHead>

                  {!!rules?.length && (
                    <TableLegacyTBody>
                      {rules.map((rule) => (
                        <TableRow caseSizeRule={rule} key={rule.id} />
                      ))}
                    </TableLegacyTBody>
                  )}
                </TableLegacy>
              </TableLegacyContainer>
              <Button
                onClick={() => setIsModalActive(true)}
                className={"mt-2"}
                color={"tertiary"}
              >
                <Icon icon={"PLUS_CIRCLE"} />
                Add case size rule
              </Button>
            </div>
          </CardContent>
        </Card>
        <FullCaseRequirement />
        <Card className="bg-white">
          <CardHeader className={twMerge("bg-white", "flex flex-col p-4")}>
            <H5>Allow customer collections</H5>
            <H6>
              Allow your customers to choose to collect their order from your
              business or collection address when they checkout
            </H6>
          </CardHeader>
          <CardContent className={"p-4"}>
            <RadioGroup
              value={`${collectionOnCheckout}`}
              onChange={(value) => onChange(value as CollectionOnCheckoutType)}
            >
              <RadioGroupContainer>
                <RadioGroupOption value={"business"}>
                  <div className="flex flex-col">
                    <H5>
                      Yes, allow customers to collect orders from my business
                      address
                    </H5>
                    <p>
                      All customers will have the option during checkout to
                      collect their order from your business address.
                    </p>
                  </div>
                </RadioGroupOption>
                {collectionAddressCreationEnabled && (
                  <RadioGroupOption value={"collection"}>
                    <div className="flex flex-col">
                      <H5>
                        Yes, allow customers to collect orders from my
                        collection address
                      </H5>
                      <p>
                        All customers will have the option during checkout to
                        collect their order from your collection address.
                      </p>
                    </div>
                  </RadioGroupOption>
                )}
                <RadioGroupOption value={"none"}>
                  <div className="flex flex-col">
                    <H5>
                      No don't allow customers to collect orders from either
                      address
                    </H5>
                    <p>
                      Only customers you have explicitly assigned 'collection'
                      to will have the option during checkout to collect their
                      order. Agree a collection address directly with these
                      customers.
                    </p>
                  </div>
                </RadioGroupOption>
              </RadioGroupContainer>
            </RadioGroup>
          </CardContent>
        </Card>
      </div>

      <CaseSizeRuleCreateDialog
        active={isModalActive}
        onDismiss={() => setIsModalActive(false)}
      />
    </Delivery>
  );
};
